import type { StudyData, StudyPiece } from "./types";

export class ApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  const payload = await response.json().catch(() => null) as ({ error?: string } & T) | null;
  if (!response.ok) throw new ApiError(payload?.error ?? "Request failed", response.status);
  if (payload === null) throw new ApiError("Invalid server response", response.status);
  return payload as T;
}

export function login(nickname: string, password: string): Promise<{ ok: boolean; completed: boolean }> {
  return request("/api/session", { method: "POST", body: JSON.stringify({ nickname, password }) });
}

export function loadStudy(): Promise<StudyData> {
  return request("/api/study");
}

function piecePayload(piece: StudyPiece): string {
  return JSON.stringify({
    responses: piece.candidates.map((candidate) => ({
      candidateId: candidate.id,
      score: candidate.score,
      listenedMs: candidate.listenedMs,
    })),
  });
}

export function savePiece(piece: StudyPiece): Promise<{ ok: boolean }> {
  return request(`/api/pieces/${encodeURIComponent(piece.id)}`, {
    method: "PUT",
    body: piecePayload(piece),
  });
}

export function savePieceOnExit(piece: StudyPiece): void {
  void fetch(`/api/pieces/${encodeURIComponent(piece.id)}`, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: piecePayload(piece),
    keepalive: true,
  });
}

export function completeStudy(): Promise<{ ok: boolean; completed: boolean }> {
  return request("/api/complete", { method: "POST" });
}

export function logout(): Promise<{ ok: boolean }> {
  return request("/api/session", { method: "DELETE" });
}
